import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  ChartOfAccountStyles,
  money,
  num,
} from "../Chart-Of-Account/ChartOfAccountShared";

const List = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const showAlert = (message, type) => {
    setAlert({ message: message, type: type });
    setTimeout(() => setAlert(null), 4000);
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await Http.get(`/acc-customer`);
      // Formatted here as well as in the column bodies, so the CSV/PDF export
      // carries the same text the table shows.
      setReceiveData(
        (res.data || []).map((item) => ({
          ...item,
          opening_balance_display: money(item.opening_balance),
          credit_limit_display: money(item.credit_limit),
          charged_to: `${item.cost_center_count} / ${item.project_count}`,
        })),
      );
    } catch (error) {
      console.error("Fetch error:", error);
      showAlert(
        "Could not load customers. Please refresh and try again.",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const deleteData = (item) => {
    Swal.fire({
      title: "Delete this customer?",
      text: `"${item.code} - ${item.name}" will be removed permanently.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      Http.delete("/acc-customer/" + item.id)
        .then((res) => {
          showAlert(res.data.message, res.data.type || "success");
          fetchData();
        })
        .catch((e) => {
          // A customer that still owns cost centers or projects comes back
          // as a 400 carrying the counts.
          Swal.fire(
            "Not deleted",
            e.response?.data?.message ||
              "Could not delete the customer. Please try again.",
            "warning",
          );
        });
    });
  };

  // Counted over every record: the table's status filter starts on "active",
  // so these also say what that filter is hiding.
  const stats = useMemo(() => {
    const rows = receiveData || [];
    const active = rows.filter((row) => row.status === "active");
    return {
      total: rows.length,
      active: active.length,
      inactive: rows.length - active.length,
      opening: active.reduce((sum, row) => sum + num(row.opening_balance), 0),
      creditLimit: active.reduce((sum, row) => sum + num(row.credit_limit), 0),
      costCenters: rows.reduce(
        (sum, row) => sum + num(row.cost_center_count),
        0,
      ),
      projects: rows.reduce((sum, row) => sum + num(row.project_count), 0),
    };
  }, [receiveData]);

  const codeBodyTemplate = (rowData) => (
    <span className="coa-code">{rowData.code}</span>
  );

  const nameBodyTemplate = (rowData) => (
    <Link
      to={"/view-acc-customer/" + rowData.id}
      style={{ fontWeight: 600, color: "#2a2962" }}
    >
      {rowData.name}
    </Link>
  );

  const contactBodyTemplate = (rowData) => {
    if (!rowData.contact_person && !rowData.phone) {
      return <span style={{ color: "#b6bacb" }}>—</span>;
    }
    return (
      <div style={{ lineHeight: 1.35 }}>
        <div>{rowData.contact_person || "—"}</div>
        {rowData.phone && (
          <div
            className="coa-mono"
            style={{ fontSize: "0.78rem", color: "#8b90a5" }}
          >
            {rowData.phone}
          </div>
        )}
      </div>
    );
  };

  const amountBodyTemplate = (field) => {
    const Component = (rowData) => {
      const value = num(rowData[field]);
      return (
        <span
          className="coa-num"
          style={{ display: "block", color: value ? "#35374d" : "#b6bacb" }}
        >
          {money(value)}
        </span>
      );
    };
    Component.displayName = `AmountBodyTemplate(${field})`;
    return Component;
  };

  const chargedBodyTemplate = (rowData) => (
    <span className="d-inline-flex gap-1">
      <span
        className={`coa-pill ${
          num(rowData.cost_center_count) ? "coa-pill-dr" : "coa-pill-muted"
        }`}
        title="Cost centers"
      >
        <i className="fa fa-bullseye mg-r-4"></i>
        {num(rowData.cost_center_count)}
      </span>
      <span
        className={`coa-pill ${
          num(rowData.project_count) ? "coa-pill-cr" : "coa-pill-muted"
        }`}
        title="Projects"
      >
        <i className="fa fa-briefcase mg-r-4"></i>
        {num(rowData.project_count)}
      </span>
    </span>
  );

  const cols = [
    { field: "code", header: "Code", body: codeBodyTemplate },
    { field: "name", header: "Name", body: nameBodyTemplate },
    { field: "contact_person", header: "Contact", body: contactBodyTemplate },
    {
      field: "opening_balance_display",
      sortField: "opening_balance",
      header: "Opening Balance",
      body: amountBodyTemplate("opening_balance"),
      headerClassName: "coa-num",
    },
    {
      field: "credit_limit_display",
      sortField: "credit_limit",
      header: "Credit Limit",
      body: amountBodyTemplate("credit_limit"),
      headerClassName: "coa-num",
    },
    {
      field: "charged_to",
      header: "Centers / Projects",
      body: chargedBodyTemplate,
      sortable: false,
    },
    // "Status" (exact) turns on ReactDataTable's active/inactive tag and filter.
    { field: "status", header: "Status" },
  ];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Customers</h5>
            <p className="text-muted mb-0 tx-12">
              Customers that cost centers and projects are charged to
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/create-acc-customer" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Customer
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat is-ok">
                <i className="fa fa-users coa-stat-icon"></i>
                <span className="coa-stat-label">Active Customers</span>
                <span className="coa-stat-value">{stats.active}</span>
                <span className="coa-stat-sub">
                  {stats.inactive > 0
                    ? `${stats.inactive} inactive, hidden by the status filter`
                    : `${stats.total} in total`}
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-flag-o coa-stat-icon"></i>
                <span className="coa-stat-label">Opening Balances</span>
                <span className="coa-stat-value">{money(stats.opening)}</span>
                <span className="coa-stat-sub">Across active customers</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-credit-card coa-stat-icon"></i>
                <span className="coa-stat-label">Credit Extended</span>
                <span className="coa-stat-value">
                  {money(stats.creditLimit)}
                </span>
                <span className="coa-stat-sub">Sum of credit limits</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="coa-stat">
                <i className="fa fa-sitemap coa-stat-icon"></i>
                <span className="coa-stat-label">Charged Work</span>
                <span className="coa-stat-value">
                  {stats.costCenters + stats.projects}
                </span>
                <span className="coa-stat-sub">
                  {stats.costCenters} cost centers · {stats.projects} projects
                </span>
              </div>
            </div>
          </div>

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>All Customers
              </span>
            </div>
            <div className="coa-panel-body">
              <ReactDataTable
                cols={cols}
                receiveData={receiveData}
                isLoading={loading}
                actionTemplate={actionTemplate}
                deleteData={deleteData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const actionTemplate = (item, deleteData) => {
  const owned = num(item.cost_center_count) + num(item.project_count);

  return (
    <div className="d-flex gap-1">
      <Link
        to={{ pathname: "/view-acc-customer/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="Cost centers & projects"
      >
        <i className="fa fa-sitemap" aria-hidden="true"></i>
      </Link>
      <Link
        to={{ pathname: "/edit-acc-customer/" + item.id }}
        className="btn btn-outline-secondary btn-sm"
        title="Edit customer"
      >
        <i className="fa fa-pencil" aria-hidden="true"></i>
      </Link>
      {/* The server refuses to delete a customer that still owns work, so the
          button says why up front. */}
      <span
        title={
          owned > 0
            ? "Move or delete its cost centers and projects first"
            : "Delete customer"
        }
      >
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={() => deleteData(item)}
          disabled={owned > 0}
          style={owned > 0 ? { pointerEvents: "none" } : undefined}
        >
          <i className="fa fa-trash" aria-hidden="true"></i>
        </button>
      </span>
    </div>
  );
};

export default List;
